/**
 * Order-level charges editor.
 *
 * Charges that belong to the order rather than to any one line — a documentation
 * fee, a handling fee, a delivery charge covering the whole shipment. They live
 * in `order_tax_lines` alongside tax, separated by `charge_class`, so this drawer
 * reads that field rather than assuming every row in the array is tax.
 *
 * Two kinds of row appear here and they are not equally editable. A row a scheme
 * produced is recomputed from the scheme every time the order changes, so
 * deleting it would only bring it back; the way to remove one is to stop the
 * scheme matching. A row somebody typed carries `override_mode: "manual_charge"`
 * and can be deleted, which is exactly the filter the server enforces on its own
 * delete route. That flag is anti-deletion plumbing, not a user override, so it
 * is deliberately not rendered as "Overridden".
 */

import { useState } from "react";
import { Plus, Receipt, Trash2 } from "lucide-react";

import ActionDrawer from "@/components/ui/ActionDrawer";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import { CHARGE_TYPES, DEFAULT_CHARGE_TYPE } from "@/components/ui/chargeTypes";
import { splitChargeRows } from "@/utils/taxDisplay";
import type { OrderTaxLine } from "@/types/sales-order";

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(v);

export interface NewOrderCharge {
  charge_type: string;
  description: string;
  amount: number;
  is_taxable: boolean;
}

interface OrderChargesDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** The order's full tax-line array; fees are split out here. */
  taxLines: OrderTaxLine[];
  /** Line-level charges are shown as context, since they share the Fees total. */
  lineChargesTotal: number;
  canEdit: boolean;
  isAdding: boolean;
  isDeleting: boolean;
  onAdd: (data: NewOrderCharge) => void;
  onDelete: (chargeId: string) => void;
}

export default function OrderChargesDrawer({
  isOpen,
  onClose,
  taxLines,
  lineChargesTotal,
  canEdit,
  isAdding,
  isDeleting,
  onAdd,
  onDelete,
}: OrderChargesDrawerProps) {
  const [chargeType, setChargeType] = useState(DEFAULT_CHARGE_TYPE);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [taxable, setTaxable] = useState<boolean | null>(null);

  const { fee: feeRows, feeTotal } = splitChargeRows(taxLines);
  const amountValue = parseFloat(amount);
  const canAdd =
    !!description.trim() &&
    !isNaN(amountValue) &&
    amountValue > 0 &&
    taxable !== null &&
    !isAdding;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canAdd) return;
    onAdd({
      charge_type: chargeType,
      description: description.trim(),
      amount: amountValue,
      is_taxable: taxable === true,
    });
    setDescription("");
    setAmount("");
    setChargeType(DEFAULT_CHARGE_TYPE);
    setTaxable(null);
  };

  const fieldClass =
    "mt-1 block w-full h-[34px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-sm text-[var(--text-primary)]";
  const labelCls =
    "block text-[11px] font-medium uppercase tracking-wider text-[var(--text-tertiary)]";

  return (
    <ActionDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Charges"
      description="Delivery, documentation and handling charged on this order"
      icon={Receipt}
      size="md"
    >
      <div className="space-y-6">
        <div>
          <div className="mb-3 flex items-center justify-between border-b border-[var(--border-subtle)] pb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
              Order-level
            </span>
            <span className="text-sm font-medium tabular-nums text-[var(--text-primary)]">
              {fmt(feeTotal)}
            </span>
          </div>

          {feeRows.length === 0 ? (
            <p className="py-2 text-xs text-[var(--text-tertiary)]">
              No order-level charges. Anything added here applies to the whole
              order rather than to one line.
            </p>
          ) : (
            <div className="space-y-0">
              {feeRows.map((row) => {
                // Only a hand-entered charge can be deleted — a scheme's row is
                // regenerated on the next recalculation.
                const isManual = row.override_mode === "manual_charge";
                return (
                  <div
                    key={row.id}
                    className="flex items-center gap-2 border-b border-[var(--border-subtle)]/50 py-2 last:border-0"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-sm text-[var(--text-primary)]">
                          {row.scheme_name}
                        </span>
                        {!isManual && <Badge variant="info">scheme</Badge>}
                        {row.is_taxable && (
                          <Badge variant="warning">taxable</Badge>
                        )}
                      </div>
                      {!isManual && (
                        <p className="mt-0.5 text-[11px] text-[var(--text-tertiary)]">
                          Applied automatically — change the scheme to remove
                          it.
                        </p>
                      )}
                    </div>
                    <span className="shrink-0 text-sm tabular-nums text-[var(--text-warning)]">
                      {fmt(Number(row.applied_tax))}
                    </span>
                    <button
                      type="button"
                      onClick={() => onDelete(row.id)}
                      disabled={!canEdit || !isManual || isDeleting}
                      title={
                        isManual
                          ? "Remove this charge"
                          : "Scheme charges are recalculated, not deleted"
                      }
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-tertiary)] transition-colors hover:bg-[var(--fill-danger)] hover:text-[var(--text-danger)] disabled:pointer-events-none disabled:opacity-30"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {lineChargesTotal > 0 && (
            <p className="mt-3 rounded-[var(--radius-md)] bg-[var(--surface-sunken)] px-3 py-2 text-[11px] text-[var(--text-tertiary)]">
              A further {fmt(lineChargesTotal)} of charges sits on individual
              lines and is edited there. The order's Fees total adds both
              together.
            </p>
          )}
        </div>

        {canEdit && (
          <form onSubmit={submit}>
            <div className="mb-3 flex items-center gap-2 border-b border-[var(--border-subtle)] pb-2">
              <Plus className="h-4 w-4 text-[var(--text-tertiary)]" />
              <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                Add a charge
              </span>
            </div>

            <div className="space-y-3 rounded-[var(--radius-md)] bg-[var(--surface-sunken)] p-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Type</label>
                  <select
                    value={chargeType}
                    onChange={(e) => setChargeType(e.target.value)}
                    className={fieldClass}
                  >
                    {CHARGE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className={`${fieldClass} text-right tabular-nums`}
                  />
                </div>
              </div>

              <div>
                <label className={labelCls}>Description</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What the customer is being charged for"
                  className={fieldClass}
                />
              </div>

              <div>
                <label className={labelCls}>Taxable</label>
                <select
                  value={taxable === null ? "" : String(taxable)}
                  onChange={(e) =>
                    setTaxable(
                      e.target.value === "" ? null : e.target.value === "true",
                    )
                  }
                  className={`${fieldClass} ${taxable === null ? "text-[var(--text-tertiary)]" : ""}`}
                >
                  <option value="">Choose...</option>
                  <option value="true">Taxable</option>
                  <option value="false">Not taxable</option>
                </select>
                <p className="mt-1 text-[11px] text-[var(--text-tertiary)]">
                  Whether the charge itself is taxed varies by state, so there
                  is no default — a wrong guess is the seller's liability at
                  audit.
                </p>
              </div>
            </div>

            <div className="mt-4 flex justify-end border-t border-[var(--border-subtle)] pt-4">
              <Button
                type="submit"
                size="sm"
                isLoading={isAdding}
                disabled={!canAdd}
              >
                Add charge
              </Button>
            </div>
          </form>
        )}
      </div>
    </ActionDrawer>
  );
}
